/**
 * Rio's interviewer for a job, as the recruiter shapes it.
 *
 * The shapes avrio-backend returns from /api/interviewer and
 * /api/jobs/{jobId}/interviewer. Rio (avrio-rio) owns the meaning: it drafts a
 * config from the posting, applies plain-language edits as a fixed set of
 * operations, validates every change, and builds the interview from the saved
 * config. This app only shows and edits it.
 */

export const SECTION_KEYS = [
  'logistics',
  'skills',
  'workspace',
  'ai_fluency',
  'candidate_questions',
] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export const TONES = ['warm', 'neutral', 'brisk'] as const;
export type Tone = (typeof TONES)[number];

export const WORKSPACE_KINDS = [
  'auto',
  'code',
  'whiteboard',
  'design',
] as const;
export type WorkspaceKind = (typeof WORKSPACE_KINDS)[number];

export interface InterviewerSection {
  key: SectionKey;
  enabled: boolean;
  /** Optional on the wire; the page always fills it in (from Rio's preview). */
  minutes: number;
}

/** What a strong answer shows, as Rio sketches it (schema 2). */
export interface QuestionExpectations {
  excellent: string[];
  adequate: string[];
  weak: string[];
}

export const EXPECTATION_LEVELS = ['excellent', 'adequate', 'weak'] as const;
export type ExpectationLevel = (typeof EXPECTATION_LEVELS)[number];

export interface CustomQuestion {
  id: string;
  text: string;
  section: SectionKey;
  followUp: boolean;
  /** One of the job's areas, chosen by Rio; the recruiter can change it. */
  area: string | null;
  areaLabel: string | null;
  /** Null until Rio has sketched the bar. */
  expectations: QuestionExpectations | null;
  /** False: asked and shared, "for information only", not scored. */
  counts: boolean;
}

export interface InterviewerConfig {
  schema: number;
  /** All five sections, once each: reordered and switched, never added or removed. */
  sections: InterviewerSection[];
  /** Skill keys from the posting (or Rio's areas), at most five each. */
  emphasize: string[];
  deemphasize: string[];
  customQuestions: CustomQuestion[];
  avoidTopics: string[];
  tone: Tone;
  workspace: { enabled: boolean; kind: WorkspaceKind };
  targetMinutes: number;
  /** Stamped by the server on save. */
  meta: {
    createdFrom: string;
    updatedBy: string | null;
    updatedAt: string | null;
    version: number | null;
  };
}

export interface SkillRef {
  key: string;
  label: string;
}

export interface PreviewSection {
  key: SectionKey;
  title: string;
  minutes: number;
  enabled: boolean;
  summary: string;
  /** Examples only: each candidate's questions adapt to their own gaps. */
  exampleQuestions: string[];
}

export interface InterviewerPreview {
  totalMinutes: number;
  sections: PreviewSection[];
  workspace: {
    enabled: boolean;
    kind: WorkspaceKind;
    label: string;
    /** Set when the chosen kind isn't available yet, e.g. "coming soon". */
    note: string | null;
  };
  emphasis: { emphasize: SkillRef[]; deemphasize: SkillRef[] };
  customQuestions: PreviewQuestion[];
  notes: string[];
  /** The job's areas a question can be linked to, when Rio sends them. */
  areas: SkillRef[];
}

export interface PreviewQuestion {
  id: string;
  text: string;
  section: SectionKey;
  area: string | null;
  areaLabel: string | null;
  expectations: QuestionExpectations | null;
  counts: boolean;
}

export interface PostingSkill {
  key: string;
  label: string;
  required: boolean;
}

/** What Rio drafts from: the posting as the recruiter has written it so far. */
export interface Posting {
  title: string;
  description: string;
  skills: PostingSkill[];
}

export interface AppliedChange {
  op: string;
  summary: string;
}

export interface RejectedChange extends AppliedChange {
  reason: string;
}

export interface DiffEntry {
  path: string;
  before: unknown;
  after: unknown;
}

export interface DraftResult {
  config: InterviewerConfig;
  preview: InterviewerPreview;
}

export interface EditResult extends DraftResult {
  applied: AppliedChange[];
  rejected: RejectedChange[];
  diff: DiffEntry[];
}

export interface SavedInterviewer {
  /** Null means the job uses Rio's default interviewer. */
  config: InterviewerConfig | null;
  /** The version to save against next; null for a job never saved. */
  version: number | null;
  /** With no config, Rio's default draft for the posting. */
  preview: InterviewerPreview;
}

export interface SaveResult {
  config: InterviewerConfig;
  version: number | null;
  preview: InterviewerPreview;
}

export interface ValidationResult {
  valid: boolean;
  problems: FieldError[];
  preview: InterviewerPreview | null;
}

export interface HistoryVersion {
  version: number;
  updatedAt: string;
  updatedBy: string;
  /** Worked out on the page from the change against the version before. */
  summary: string;
}

export interface FieldError {
  path: string;
  message: string;
}

export interface InterviewerService {
  /** Not saved. Works before the job exists. */
  draft(posting: Posting): Promise<DraftResult>;
  /** Not saved: the recruiter keeps or undoes the result. */
  edit(
    config: InterviewerConfig,
    instruction: string,
    posting: Posting,
  ): Promise<EditResult>;
  get(jobId: number): Promise<SavedInterviewer>;
  /** Rejects with a `conflict` error when someone else saved first. */
  save(
    jobId: number,
    config: InterviewerConfig,
    expectedVersion: number | null,
  ): Promise<SaveResult>;
  /** Back to Rio's default interviewer. History is kept. */
  reset(jobId: number): Promise<{ version: number | null }>;
  history(jobId: number): Promise<HistoryVersion[]>;
  /** A quick check, e.g. of a custom question as it's typed. */
  validate(
    config: InterviewerConfig,
    posting?: Posting,
  ): Promise<ValidationResult>;
}
